import React from 'react';
import { Link } from 'react-router-dom';
import { useLang } from '@/i18n/LanguageContext';
import { useAuth } from '@/auth/AuthContext';
import PageDetailsAccordion from '@/components/PageDetailsAccordion';
import { EditorialPage, EditorialCards, EditorialLinks } from '@/components/Editorial';

const textLink = 'text-[#557448] underline underline-offset-4 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4';
const Section = ({ title, children, testId }) => <section className="mt-8 min-w-0" data-testid={testId}><h2 className="font-serif text-2xl sm:text-3xl text-[#2A2521] mb-4">{title}</h2>{children}</section>;

export default function HowItWorks() {
  const { t } = useLang();
  const { user, isChecking } = useAuth();
  const stepDestinations = ['/explore', '/atlas', '/explore'];
  const steps = t('howPage.steps').map((step, index) => ({ ...step, ...(stepDestinations[index] ? { to: stepDestinations[index] } : {}) }));
  return <EditorialPage testId="how-it-works-page" title={t('howPage.title')} intro={<>
    <span className="block font-serif text-2xl text-[#4A443D] mb-3">{t('howPage.subtitle')}</span>
    <span className="block">{t('howPage.intro')}</span>
  </>}>
    <EditorialLinks links={[{ to: '/atlas', label: t('howPage.atlas') }, { to: '/explore', label: t('howPage.opportunities') }]} />
    <Section title={t('howPage.stepsTitle')} testId="how-steps">
      <EditorialCards symbols={['territory', 'citizen', 'document', 'choices']} items={steps} />
    </Section>
    <Section title={t('howPage.formulasTitle')} testId="how-formulas">
      <EditorialCards symbols={['benefits', 'heart', 'contribution', 'handshake']} items={t('howPage.formulas')} />
      <p className="mt-4 text-[#4A443D]">{t('howPage.noPro')}</p>
      <p className="mt-3 text-sm"><Link className={textLink} to="/plans#pro-payments-fees">{t('howPage.promoterLink')}</Link></p>
    </Section>
    <Section title={t('howPage.detailsTitle')} testId="how-details">
      <PageDetailsAccordion panels={[
        { id: 'participation-options', title: t('howPage.optionsDetailsTitle'), children: <>{t('howPage.otherParagraphs').map(paragraph => <p key={paragraph}>{paragraph}</p>)}</> },
        { id: 'account-payments', title: t('howPage.accountDetailsTitle'), children: <>
          {t('howPage.questions').slice(0, 2).map(({ answer }) => <p key={answer}>{answer}</p>)}
          <p><Link className={textLink} to="/payments/history">{t('howPage.paymentHistory')}</Link></p>
        </> },
        { id: 'project-goals-refunds', title: t('howPage.goalsDetailsTitle'), children: <>
          <p>{t('howPage.projectGoal')}</p>
          <p>{t('howPage.questions')[2].answer}</p>
          <p><Link className={textLink} to="/projects">{t('howPage.projects')}</Link></p>
          <p><Link className={textLink} to="/subscription-terms">{t('howPage.paymentTerms')}</Link></p>
        </> },
        { id: 'promoters-proposals', title: t('howPage.promotersDetailsTitle'), children: <>
          <p>{t('howPage.transparency')}</p>
          <p>{t('howPage.permissions')}</p>
          <p>{t('howPage.platformSupport')}</p>
        </> },
      ]} />
    </Section>
    <aside className="mt-6 rounded-md border border-[#E2DACB] bg-[#EAF0E6] p-4" aria-label={t('howPage.transparencyTitle')} data-testid="how-transparency">
      <p className="text-[#4A443D]">{t('howPage.transparencyShort')}</p>
      <div className="flex flex-wrap gap-x-5 gap-y-2 mt-3 text-sm"><Link className={textLink} to="/terms">{t('howPage.terms')}</Link><Link className={textLink} to="/subscription-terms">{t('howPage.paymentTerms')}</Link><Link className={textLink} to="/faq">{t('howPage.faq')}</Link></div>
    </aside>
    <Section title={t('howPage.finalTitle')} testId="how-final">
      <EditorialLinks links={[
        { to: '/explore', label: t('howPage.exploreOpportunities') },
        ...(!user && !isChecking && user !== null ? [{ to: '/register', label: t('howPage.register') }] : []),
        { to: '/faq', label: t('howPage.faq') },
      ]} />
    </Section>
  </EditorialPage>;
}
